import { Trans, useTranslation } from 'react-i18next';

import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';

import { useArchiveClient } from '../../hooks';
import { Client } from '../../types';

interface ArchiveClientModalProps {
  client: Client;
  onSuccess?: () => void;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

/**
 * A client archiving modal with simplified workflow (no date picker required).
 * Provides controlled archiving workflow with clear user feedback.
 *
 * **Key Features:**
 * - **Simplified Archiving**: Direct archiving without date selection (per client requirements)
 * - **Dual State Management**: Supports both internal and external modal state control
 * - **Comprehensive UX**: Clear messaging about archiving consequences and process
 * - **API Integration**: Handles async archiving operations with proper error states
 * - **Data Consistency**: Invalidates relevant query caches after successful archiving
 * - **Accessibility**: Full keyboard navigation and screen reader support
 *
 * **Archive Process:**
 * 1. User confirms archive action
 * 2. Modal explains what happens during archiving
 * 3. Confirmation triggers API call
 * 4. Success invalidates client caches and closes modal
 * 5. Error keeps modal open for retry
 *
 * @example
 * ```tsx
 * // Basic usage with internal state management
 * <ArchiveClientModal
 *   client={selectedClient}
 *   onSuccess={() => {
 *     toast.success('Client archived successfully');
 *     navigate('/clients');
 *   }}
 * />
 *
 * // With custom trigger button
 * <ArchiveClientModal
 *   client={client}
 *   trigger={
 *     <Button variant="outline" size="sm">
 *       <ArchiveIcon className="w-4 h-4 mr-2" />
 *       Archive This Client
 *     </Button>
 *   }
 *   onSuccess={() => refetchClients()}
 * />
 *
 * // External state management (for complex flows)
 * function ClientManagement() {
 *   const [showArchiveModal, setShowArchiveModal] = useState(false);
 *   const [selectedClient, setSelectedClient] = useState<Client | null>(null);
 *
 *   const handleArchiveClick = (client: Client) => {
 *     setSelectedClient(client);
 *     setShowArchiveModal(true);
 *   };
 *
 *   return (
 *     <>
 *       <ClientList onArchive={handleArchiveClick} />
 *
 *       {selectedClient && (
 *         <ArchiveClientModal
 *           client={selectedClient}
 *           open={showArchiveModal}
 *           onOpen={() => setShowArchiveModal(true)}
 *           onClose={() => {
 *             setShowArchiveModal(false);
 *             setSelectedClient(null);
 *           }}
 *           onSuccess={() => {
 *             setShowArchiveModal(false);
 *             setSelectedClient(null);
 *             queryClient.invalidateQueries(['clients']);
 *           }}
 *         />
 *       )}
 *     </>
 *   );
 * }
 * ```
 */
export function ArchiveClientModal({
  client,
  onSuccess,
  trigger,
  open,
  onOpen,
  onClose,
}: ArchiveClientModalProps) {
  const { t } = useTranslation(['authenticated.clients', 'common']);

  const { mutate, isPending } = useArchiveClient({
    clientId: client.id,
    onSuccess: () => {
      onSuccess?.();
      onClose?.();
    },
  });

  const handleArchive = () => {
    mutate();
  };

  const messageContent = (
    <div className="space-y-4">
      <div>
        <Trans
          i18nKey="authenticated.clients:archive.archiveClientMessage"
          components={{ strong: <strong /> }}
        />
      </div>
      <div className="space-y-2">
        <p className="font-semibold">{t('authenticated.clients:archive.whatHappens')}</p>
        <ul className="ml-2 list-inside list-disc space-y-1">
          <li>{t('authenticated.clients:archive.clientWillBeHidden')}</li>
          <li>{t('authenticated.clients:archive.dataWillBePreserved')}</li>
          <li>{t('authenticated.clients:archive.canBeRestored')}</li>
        </ul>
      </div>
    </div>
  );

  return (
    <ConfirmationModal
      trigger={trigger}
      title={t('authenticated.clients:archive.archiveClientTitle')}
      message={messageContent}
      confirmText={
        isPending
          ? t('authenticated.clients:archive.archiving')
          : t('authenticated.clients:archive.archive')
      }
      confirmVariant="default"
      onConfirm={handleArchive}
      isLoading={isPending}
      open={open}
      onOpen={onOpen}
      onClose={onClose}
    />
  );
}
